import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        // Check localStorage first, then system preference
        const savedTheme = localStorage.getItem("wishliz-theme");
        if (savedTheme) {
            return savedTheme === "dark";
        }
        // Check system preference
        return (
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
        );
    });

    const toggleTheme = () => {
        setIsDarkMode((prev) => {
            const newTheme = !prev;
            localStorage.setItem("wishliz-theme", newTheme ? "dark" : "light");
            return newTheme;
        });
    };

    useEffect(() => {
        // Apply theme to document
        if (isDarkMode) {
            document.documentElement.setAttribute("data-bs-theme", "dark");
            document.body.classList.add("dark-mode");
        } else {
            document.documentElement.setAttribute("data-bs-theme", "light");
            document.body.classList.remove("dark-mode");
        }
    }, [isDarkMode]);

    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (e) => {
            // Only update if user hasn't manually set a preference
            if (!localStorage.getItem("wishliz-theme")) {
                setIsDarkMode(e.matches);
            }
        };

        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);

    const value = {
        isDarkMode,
        toggleTheme,
        theme: isDarkMode ? "dark" : "light",
    };

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
};
